import Layout from "../../components/layout/Layout";
import React from "react";


export default function CookiePolicy(props) {
  const {
    companyName = "Predixio Technologies Ltd.",
    productName = "Predixio",
    domain = "predixio.com",
    contactEmail = "privacy@predixio.com",
    lastUpdated = new Date().toISOString().slice(0, 10),
    cookieSettingsPath = "/cookies/preferences",
  } = props;

  const ui = {
    page: { background: "#f7f7f9", minHeight: "100vh", padding: "40px 16px" },
    container: {
      maxWidth: 1400,
      margin: "0 auto",
      display: "grid",
      gap: 24,
      boxSizing: "border-box",
    },
    card: {
      background: "#fff",
      border: "1px solid rgba(0,0,0,.08)",
      borderRadius: 14,
      boxShadow: "0 8px 24px rgba(0,0,0,.05)",
      overflow: "hidden",
    },
    sidebar: { padding: 16 },
    main: { padding: 28 },
    title: { margin: 0, fontSize: 28, fontWeight: 800, color: "#0f172a" },
    subtitle: { marginTop: 6, fontSize: 14, color: "#6b7280" },
    sectionTitle: { fontSize: 18, fontWeight: 800, margin: "28px 0 10px", color: "#111827" },
    p: { margin: "10px 0", color: "#334155", lineHeight: 1.7 },
    li: { margin: "6px 0", color: "#334155", lineHeight: 1.6 },
    badge: {
      display: "inline-block",
      fontSize: 12,
      color: "#d97706",
      background: "rgba(217,119,6,.10)",
      border: "1px solid rgba(217,119,6,.25)",
      padding: "4px 10px",
      borderRadius: 999,
      marginRight: 8,
    },
    hr: { border: "none", borderTop: "1px solid rgba(0,0,0,.06)", margin: "20px 0" },
    tocTitle: { fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 8 },
    tocLink: { display: "block", fontSize: 13, color: "#334155", padding: "6px 8px", borderRadius: 8, textDecoration: "none" },
    tocLinkActive: { background: "rgba(217,119,6,.10)", color: "#b45309", border: "1px solid rgba(217,119,6,.25)" },
    callout: {
      background: "linear-gradient(180deg, #fff, #fafafa)",
      border: "1px dashed rgba(0,0,0,.15)",
      borderRadius: 12,
      padding: 12,
      color: "#374151",
      fontSize: 13,
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      fontSize: 13,
      border: "1px solid rgba(0,0,0,.08)",
      borderRadius: 12,
    },
    th: { textAlign: "left", background: "#fafafa", padding: "10px 12px", borderBottom: "1px solid rgba(0,0,0,.06)" },
    td: { padding: "10px 12px", borderBottom: "1px solid rgba(0,0,0,.06)", verticalAlign: "top", color: "#334155" },
    code: {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
      fontSize: 12,
      background: "#f3f4f6",
      borderRadius: 6,
      padding: "2px 6px",
      wordBreak: "break-word",
    },
  };

  const sections = [
    { id: "intro", label: "1. About this Cookie Policy" },
    { id: "what", label: "2. What Are Cookies & Similar Tech" },
    { id: "types", label: "3. Categories We Use" },
    { id: "list", label: "4. Examples of Cookies We Use" },
    { id: "consent", label: "5. Your Choices & Consent" },
    { id: "manage", label: "6. Managing Cookies in Your Browser" },
    { id: "third", label: "7. Third-Party Cookies" },
    { id: "analytics", label: "8. Analytics, Ads & Social" },
    { id: "signals", label: "9. Do Not Track & GPC" },
    { id: "retention", label: "10. Retention" },
    { id: "changes", label: "11. Changes to this Policy" },
    { id: "contact", label: "12. Contact" },
  ];

  const [active, setActive] = React.useState(sections[0].id);
  React.useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: [0, 1] }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  const A = ({ href, children }) => (
    <a href={href} style={{ color: "#b45309", textDecoration: "none", wordBreak: "break-word" }}>
      {children}
    </a>
  );

  return (
    <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="Cookie Policy">
      <style>{`
  /* base */
  .cp-page, .cp-container, .cp-card, .cp-main {
    box-sizing: border-box; max-width: 100%; overflow-x: clip;
  }

  /* desktop: 2 columns */
  .cp-container {
    display: grid;
    grid-template-columns: 280px 1fr;
    grid-template-areas: "sidebar main";
    gap: 24px;
  }
  .cp-sidebar { grid-area: sidebar; position: sticky; top: 24px; align-self: start; }
  .cp-main    { grid-area: main; }
  .cp-toc a { white-space: normal; }
  .cp-main img, .cp-main table, .cp-main code { max-width: 100%; }
  .cp-table-wrap { overflow-x: auto; }

  /* tablet */
  @media (max-width: 1024px) {
    .cp-container { grid-template-columns: 240px 1fr; gap: 20px; }
  }

  /* mobile: stack one-after-another (sidebar then content) */
  @media (max-width: 768px) {
    .cp-container {
      grid-template-columns: 1fr;
      grid-template-areas:
        "sidebar"
        "main";
      gap: 16px;
    }
    .cp-sidebar { position: static; top: auto; width: 100%; }
    .cp-title { font-size: 22px !important; }
  }
`}</style>

      <div className="cp-page" style={ui.page}>
        <div className="cp-container" style={ui.container}>
          <aside className="cp-card cp-sidebar" style={{ ...ui.card, ...ui.sidebar }}>
            <div style={ui.tocTitle}>Cookie Policy</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
              Last updated: <span style={{ fontWeight: 700 }}>{lastUpdated}</span>
            </div>
            <hr style={ui.hr} />
            <nav className="cp-toc">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  style={{ ...ui.tocLink, ...(active === s.id ? ui.tocLinkActive : null) }}
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </aside>

          <main className="cp-card cp-main" style={{ ...ui.card, ...ui.main }}>
            <div style={{ marginBottom: 8 }}>
              <span style={ui.badge}>{productName}</span>
              <span style={ui.badge}>Cookies</span>
            </div>
            <h1 className="cp-title" style={ui.title}>Cookie Policy</h1>
            <p style={ui.subtitle}>
              This Cookie Policy explains how {companyName} (“we”, “us”, “our”) uses cookies and similar technologies on <strong>{domain}</strong>, our apps, and APIs (collectively, the “Service”). It should be read with our <A href="/privacy">Privacy Policy</A> and <A href="/terms">Terms & Conditions</A>.
            </p>

            <section id="intro">
              <h2 style={ui.sectionTitle}>1. About this Cookie Policy</h2>
              <p style={ui.p}>
                We use cookies, local storage, and similar technologies to operate and secure the Service, remember preferences, analyze usage, and (if enabled) personalize content or advertising. Where required by law, we seek your consent via our cookie banner and <A href={cookieSettingsPath}>Cookie Settings</A>.
              </p>
            </section>
            <hr style={ui.hr} />

            <section id="what">
              <h2 style={ui.sectionTitle}>2. What Are Cookies & Similar Technologies</h2>
              <p style={ui.p}>
                Cookies are small text files placed on your device when you visit a site. They may be set by us (“first-party”) or by third parties (“third-party”). We also use local storage, session storage, pixels, SDKs, and tags with similar purposes.
              </p>
            </section>
            <hr style={ui.hr} />

            <section id="types">
              <h2 style={ui.sectionTitle}>3. Categories We Use</h2>
              <ul style={{ paddingLeft: 18 }}>
                <li style={ui.li}><strong>Strictly Necessary</strong> — required for core features (authentication, load balancing, security). These cannot be switched off in our systems.</li>
                <li style={ui.li}><strong>Functional</strong> — remember preferences (e.g., favorite leagues, theme, locale) to enhance your experience.</li>
                <li style={ui.li}><strong>Analytics & Performance</strong> — help us understand usage, fix issues, and improve features (e.g., page views, latency).</li>
                <li style={ui.li}><strong>Advertising & Personalization</strong> (if enabled) — measure campaigns and personalize content or ads. Requires consent where applicable.</li>
              </ul>
            </section>
            <hr style={ui.hr} />

            <section id="list">
              <h2 style={ui.sectionTitle}>4. Examples of Cookies We Use</h2>
              <div style={ui.callout}>
                The cookies and partners below are examples. The exact list may change over time. Check <A href={cookieSettingsPath}>Cookie Settings</A> for the live registry and your saved preferences.
              </div>
              <div className="cp-table-wrap">
                <table style={ui.table}>
                  <thead>
                    <tr>
                      <th style={ui.th}>Name</th>
                      <th style={ui.th}>Provider</th>
                      <th style={ui.th}>Purpose / Category</th>
                      <th style={ui.th}>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={ui.td}><code style={ui.code}>px_session</code></td>
                      <td style={ui.td}>{companyName}</td>
                      <td style={ui.td}>Session authentication & security (Strictly Necessary)</td>
                      <td style={ui.td}>Session</td>
                    </tr>
                    <tr>
                      <td style={ui.td}><code style={ui.code}>px_pref</code></td>
                      <td style={ui.td}>{companyName}</td>
                      <td style={ui.td}>UI preferences (Functional)</td>
                      <td style={ui.td}>6 months</td>
                    </tr>
                    <tr>
                      <td style={ui.td}><code style={ui.code}>_ga</code></td>
                      <td style={ui.td}>Google Analytics</td>
                      <td style={ui.td}>Analytics & performance metrics (Analytics)</td>
                      <td style={ui.td}>13 months (EU), 24 months (elsewhere)</td>
                    </tr>
                    <tr>
                      <td style={ui.td}><code style={ui.code}>_fbp</code></td>
                      <td style={ui.td}>Meta</td>
                      <td style={ui.td}>Campaign measurement / ads personalization (Advertising)</td>
                      <td style={ui.td}>3 months</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
            <hr style={ui.hr} />

            <section id="consent">
              <h2 style={ui.sectionTitle}>5. Your Choices & Consent</h2>
              <p style={ui.p}>
                On your first visit (and periodically thereafter), you may be presented with a cookie banner. You can accept all, reject non-essential, or tailor choices by category via <A href={cookieSettingsPath}>Cookie Settings</A>. Where required, we only place non-essential cookies after consent.
              </p>
              <p style={ui.p}>
                You can change your preferences at any time in <A href={cookieSettingsPath}>Cookie Settings</A>. Your consent choices are stored in a consent cookie or local storage.
              </p>
            </section>
            <hr style={ui.hr} />

            <section id="manage">
              <h2 style={ui.sectionTitle}>6. Managing Cookies in Your Browser</h2>
              <p style={ui.p}>
                Most browsers let you block or delete cookies. Instructions: Chrome, Firefox, Safari, Edge, or your device settings on iOS/Android. Blocking essential cookies may break core functionality (e.g., login, predictions dashboard).
              </p>
            </section>
            <hr style={ui.hr} />

            <section id="third">
              <h2 style={ui.sectionTitle}>7. Third-Party Cookies</h2>
              <p style={ui.p}>
                Some cookies are set by third parties (e.g., analytics, ads, social plugins, CDN). We do not control these cookies directly. Review their policies for details. We contractually require vendors to process data only for specified purposes and with appropriate safeguards.
              </p>
            </section>
            <hr style={ui.hr} />

            <section id="analytics">
              <h2 style={ui.sectionTitle}>8. Analytics, Ads & Social</h2>
              <p style={ui.p}>
                We may use analytics tools to measure performance and usage. If advertising or social pixels are enabled, they may collect identifiers to measure campaigns or personalize experiences, subject to your consent where required. You can opt out of non-essential categories in <A href={cookieSettingsPath}>Cookie Settings</A>.
              </p>
            </section>
            <hr style={ui.hr} />

            <section id="signals">
              <h2 style={ui.sectionTitle}>9. Do Not Track & Global Privacy Control (GPC)</h2>
              <p style={ui.p}>
                Your browser may transmit <strong>Do Not Track</strong> or <strong>GPC</strong> signals. Where legally required and technically feasible, we honor recognized GPC signals as an opt-out of sale/sharing or targeted advertising. You can also use <A href={cookieSettingsPath}>Cookie Settings</A> to adjust preferences.
              </p>
            </section>
            <hr style={ui.hr} />

            <section id="retention">
              <h2 style={ui.sectionTitle}>10. Retention</h2>
              <p style={ui.p}>
                Cookie lifetimes vary by purpose. Session cookies expire when you close your browser. Persistent cookies remain until their set expiration or your manual deletion. We periodically review and prune cookie usage.
              </p>
            </section>
            <hr style={ui.hr} />

            <section id="changes">
              <h2 style={ui.sectionTitle}>11. Changes to this Policy</h2>
              <p style={ui.p}>
                We may update this Cookie Policy periodically. Material changes will be communicated via the Service or email. The “Last updated” date shows the latest revision. Continued use after changes constitutes acceptance.
              </p>
            </section>
            <hr style={ui.hr} />

            <section id="contact">
              <h2 style={ui.sectionTitle}>12. Contact</h2>
              <p style={ui.p}>
                Questions about cookies? Contact us at <a href={`mailto:${contactEmail}`} style={{ color: "#b45309", textDecoration: "none" }}>{contactEmail}</a>.
              </p>
              <div style={{ ...ui.callout, marginTop: 12 }}>
                <div><strong>{productName}</strong> — <span>{domain}</span></div>
                <div><strong>Company</strong> — <span>{companyName}</span></div>
                <div><strong>Cookie Preferences</strong> — <A href={cookieSettingsPath}>{cookieSettingsPath}</A></div>
                <div><strong>Last updated</strong> — <span>{lastUpdated}</span></div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </Layout>
  );
}
